// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { useCallback, useEffect, useState } from 'react';
import { useNavigateKeepSearch } from '@hooks/useNavigateKeepSearch';
import { useTranslation } from 'react-i18next';
import { Input } from '@imprivata-cloud/components';
import { Button } from '@imprivata-cloud/design-system';

import useRequestEmailVerification from '@/self-service/hooks/useEmailVerification';
import useConfirmEmailOtp from '../../../hooks/useConfirmEmailOtp';
import { createCodingCtxV2 } from '@/api/utils';
import { RootRoutes } from '@/index';
import {
  OtpEnrollmentAlerts,
  OTP_ENROLLMENT_ALERT_KEYS,
  type AlertKeys,
  mapErrorCodeToAlert,
} from './OtpEnrollmentAlerts';

import './Otp.less';

const TRANSLATION_ROOT = 'self-service.auth-method.otp';

const OtpVerification = () => {
  const { t } = useTranslation();
  const navigateKeepSearch = useNavigateKeepSearch();
  const [codeValue, setCodeValue] = useState('');
  const [enrollmentAlert, setEnrollmentAlert] = useState<AlertKeys | null>(null);
  const [otpId, setOtpId] = useState('');

  const { mutate: requestEmailVerification } = useRequestEmailVerification();
  const { mutate: confirmEmailOtp } = useConfirmEmailOtp();

  const clearEnrollmentAlert = useCallback(() => {
    setEnrollmentAlert(null);
  }, []);

  // Send OTP
  const sendOtp = useCallback(() => {
    clearEnrollmentAlert();
    requestEmailVerification(undefined, {
      onSuccess: response => {
        setOtpId(response.otpId);
        setEnrollmentAlert(OTP_ENROLLMENT_ALERT_KEYS.SENT);
      },
      onError: error => {
        console.error('Error requesting OTP:', error);
      },
    });
  }, [requestEmailVerification, clearEnrollmentAlert]);

  useEffect(() => {
    sendOtp();
  }, [sendOtp]);

  // Buttons
  const verifyCode = async () => {
    const codingCtx = await createCodingCtxV2();
    const otpSecret = codingCtx.encryptString(codeValue);
    const codingCtxHeader = codingCtx.buildImprCodingCtxHeader();

    clearEnrollmentAlert();
    confirmEmailOtp(
      { codingCtxHeader, confirmRequest: { otpId, otp: otpSecret } },
      {
        onSuccess: () => {
          navigateKeepSearch(RootRoutes.SELF_SERVICE);
        },
        onError: error => {
          setEnrollmentAlert(mapErrorCodeToAlert(error));
          console.error('Invalid OTP:', error);
        },
      },
    );
  };

  const resendCode = () => {
    sendOtp();
  };

  const cancel = () => {
    navigateKeepSearch(RootRoutes.SELF_SERVICE);
  };

  return (
    <div className="validation">
      <OtpEnrollmentAlerts
        alertKey={enrollmentAlert}
        onClear={clearEnrollmentAlert}
        className="validation-banner"
      />

      {/* Title and description */}
      <h1 className="validation-title">{t(`${TRANSLATION_ROOT}.title-enter`)}</h1>
      <div className="validation-description">{t(`${TRANSLATION_ROOT}.description-enter`)}</div>

      {/* Otp input */}
      <div className="validation-input">
        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          value={codeValue}
          onChange={e => {
            setCodeValue(e.target.value.replace(/\D/g, ''));
          }}
        />
      </div>

      {/* Buttons */}
      <div className="validation-actions">
        <Button variant="primary" onClick={verifyCode} disabled={codeValue.length !== 6}>
          {t('common.verify')}
        </Button>
        <Button variant="default" onClick={resendCode}>
          {t(`${TRANSLATION_ROOT}.resend-code`)}
        </Button>
        <Button variant="text" onClick={cancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );
};

export default OtpVerification;
